import { Dayjs } from 'dayjs';
import { create } from 'zustand';
import { createDI } from '../../../context/di/di';
import { useApi, ApiResponse, ApiError } from '../../../api/api';
import { SectionKeys } from '../../../constant/index';
import { WorkerResponse } from '../../../model/workers_response';

/**
 * メイン画面 アクションを管理するための DI コンテキスト(アクション)
 */
export type MainAction = {
  /**
   * 作業員ID（ホバー時）
   */
  hoverWorkerId?: string;

  /**
   * 作業員IDの設定
   */
  selectedWorkerId?: { type: 'robot' | 'worker' | ''; id: string };


  /**
   * ロボットまたは従業員を選択
   * @param val
   */
  setWorkerId: (val: { type: 'robot' | 'worker' | ''; id: string }) => void;

  /**
   * 作業員IDの設定（ホバー時）
   * @param val
   */
  setHoverWorkerId: (val: string) => void;

  /**
   * 情報取得間隔
   */
  interval: NodeJS.Timeout | null;

  /**
   * 特記事項件数の取得
   * @param divisionId
   * @param sectionCode
   * @param date
   */
  getSectionCount: (divisionId: number, sectionCode: SectionKeys, date: Dayjs) => Promise<ApiResponse>;

  /**
   * ロボット情報取得開始
   * @param divisionId
   * @param floorGroupId
   * @param callback
   */
  startGetRobot: (divisionId: number, floorGroupId: number, callback: (response?: WorkerResponse, error?: ApiError) => void) => void;

  /**
   * ロボット情報取得停止
   */
  stopGetRobot: () => void;
};

/**
 * メイン画面 状態とアクションを管理するための DI コンテキスト
 */
export type MainStore = MainAction;

/**
 * メイン画面 状態とアクションを管理するための DI コンテキストを作成
 */
const [Provider, use] = createDI<MainStore>();
export { Provider as MainStoreProvider, use as useMainStore };

/**
 * メイン画面 状態とアクション
 */
export const defaultMainStore = () => {
  const api = useApi();

  return create<MainStore>((set, get) => {
    return {
      selectedWorkerId: { type: '', id: '' },
      hoverWorkerId: '',
      interval: null,

      setWorkerId: (val: { type: 'robot' | 'worker' | ''; id: string }) => {
        set({ selectedWorkerId: val });
      },

      setHoverWorkerId: (val: string) => {
        set({ hoverWorkerId: val });
      },

      getSectionCount: async (divisionId: number, sectionCode: SectionKeys, date: Dayjs) => {
        const dateStr = date.format('YYYY-MM-DD');
        const res = await api.path(`/api/works/count/divisions/${divisionId}/sections/${sectionCode}?date=${dateStr}`).get();
        return res;
      },

      startGetRobot: (divisionId: number, floorGroupId: number, callback: (resp?: WorkerResponse, error?: ApiError) => void) => {
        const { stopGetRobot } = get();
        stopGetRobot();

        const onFetch = () => {
          api.path(`/api/workers/divisions/${divisionId}/floorGroups/${floorGroupId}`).get().then((res) => {
            if (res.isError) {
              callback(undefined, new ApiError(res));
              return;
            }
            callback(res.data as WorkerResponse);
          }).catch((e) => {
            if (e instanceof ApiError) {
              callback(undefined, e);
            }
          });
        };

        const interval = setInterval(onFetch, 10000);
        onFetch();
        set({ interval });
      },

      stopGetRobot: () => {
        const { interval } = get();
        if (interval) {
          clearInterval(interval);
          set({ interval: null });
        }
      },
    };
  });
};
